import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Image, X, Loader2 } from "lucide-react";

// Add / remove bills & payment screenshots on an existing expense.
export const ExpenseFilesEditor = ({ row, canRemove, onChanged }) => {
    const [files, setFiles] = useState(row.files || []);
    const [busy, setBusy] = useState("");
    const billRef = useRef(null);
    const shotRef = useRef(null);

    const upload = async (kind, file) => {
        if (!file) return;
        setBusy(kind);
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        try {
            const r = await api.post(`/expenses/${row.id}/files`, fd);
            setFiles(r.data.files);
            onChanged();
            toast.success(`${kind === "bill" ? "Bill" : "Screenshot"} added`);
        } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
        finally { setBusy(""); }
    };
    const remove = async (f) => {
        if (!window.confirm(`Remove ${f.name}?`)) return;
        try {
            const r = await api.delete(`/expenses/${row.id}/files/${f.id}`);
            setFiles(r.data.files);
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    };

    return (
        <div className="mt-3" data-testid="expense-files-editor">
            <label className="hdb-label">Attachments</label>
            <div className="flex flex-col gap-1 mb-2">
                {files.length === 0 && <span className="text-xs text-slate-400">No files yet.</span>}
                {files.map((f, i) => (
                    <div key={f.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1"
                         data-testid={`expense-file-${i}`}>
                        <a href={`${API}/expenses/${row.id}/files/${f.id}`} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 text-blue-700 truncate">
                            {f.kind === "bill" ? <Paperclip size={10}/> : <Image size={10}/>} {f.name}
                        </a>
                        {canRemove && (
                            <button onClick={() => remove(f)} className="text-slate-400 hover:text-red-700"
                                    data-testid={`expense-file-remove-${i}`}><X size={12}/></button>
                        )}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <input ref={billRef} type="file" accept="image/*,application/pdf" className="hidden"
                       onChange={(e) => { upload("bill", e.target.files?.[0]); e.target.value = ""; }}
                       data-testid="expense-edit-bill-input"/>
                <button onClick={() => billRef.current?.click()} disabled={!!busy}
                        className="hdb-btn hdb-btn-ghost text-xs" data-testid="expense-edit-bill-btn">
                    {busy === "bill" ? <Loader2 className="animate-spin" size={12}/> : <Paperclip size={12}/>} Add bill
                </button>
                <input ref={shotRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => { upload("screenshot", e.target.files?.[0]); e.target.value = ""; }}
                       data-testid="expense-edit-shot-input"/>
                <button onClick={() => shotRef.current?.click()} disabled={!!busy}
                        className="hdb-btn hdb-btn-ghost text-xs" data-testid="expense-edit-shot-btn">
                    {busy === "screenshot" ? <Loader2 className="animate-spin" size={12}/> : <Image size={12}/>} Add screenshot
                </button>
            </div>
        </div>
    );
};
